import React, { useEffect, useState } from 'react';
import { List, Tabs, Tag, Pagination, Modal, Descriptions, Button, Radio } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import api from '../../../utils/api';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';

const { TabPane } = Tabs;

const statusMap = {
  PENDING: { color: 'orange', text: '待审批', icon: <ClockCircleOutlined /> },
  APPROVED: { color: 'green', text: '已通过', icon: <CheckCircleOutlined /> },
  REJECTED: { color: 'red', text: '已拒绝', icon: <CloseCircleOutlined /> },
  CANCELLED: { color: 'default', text: '已取消', icon: <CloseCircleOutlined /> },
};

export default function ApprovalList() {
  const [activeTab, setActiveTab] = useState('approvals');
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const { user } = useAuth();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, PENDING, HISTORY
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = user?.roleId === 1 || user?.roleId === 2;

  const fetchApprovals = async (page = 1) => {
    setLoading(true);
    try {
      const userId = user?.id;
      
      const params = {
        currentPage: page,
        pageSize: pagination.pageSize
      };

      if (activeTab === 'approvals') {
        params.applicantId = userId;
        if (statusFilter === 'PENDING') {
          params.status = 'PENDING';
        } else if (statusFilter === 'HISTORY') {
          params.status = 'HISTORY';
        }
      } else if (activeTab === 'to_approve') {
        params.status = 'PENDING';
        // Admin view: no applicantId means all
      }
      
      const res = await api.get('/approvals', { params });
      if (res && res.code === 200) {
        setApprovals(res.data.list);
        setPagination({
          ...pagination,
          current: page,
          total: res.data.totalCount || res.data.total
        });
      }
    } catch (e) {
      console.error(e);
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals(1);
  }, [activeTab, statusFilter]);

  const handlePageChange = (page) => {
    fetchApprovals(page);
  };

  const showDetail = (item) => {
    setSelectedApproval(item);
    setDetailModalVisible(true);
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;
    try {
      setActionLoading(true);
      const res = await api.post(`/approvals/${selectedApproval.id}/approve`, { remark: '同意' });
      if (res && res.code === 200) {
        Modal.success({ content: '审批已通过' });
        setDetailModalVisible(false);
        fetchApprovals(pagination.current);
      } else {
        Modal.error({ content: res.message || '操作失败' });
      }
    } catch (e) {
      console.error(e);
      Modal.error({ content: '操作异常' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval) return;
    try {
      setActionLoading(true);
      const res = await api.post(`/approvals/${selectedApproval.id}/reject`, { remark: '拒绝' });
      if (res && res.code === 200) {
        Modal.success({ content: '已拒绝该申请' });
        setDetailModalVisible(false);
        fetchApprovals(pagination.current);
      } else {
        Modal.error({ content: res.message || '操作失败' });
      }
    } catch (e) {
      console.error(e);
      Modal.error({ content: '操作异常' });
    } finally {
      setActionLoading(false);
    }
  };

  const renderPayload = (payloadStr) => {
    try {
      const obj = JSON.parse(payloadStr);
      return (
        <Descriptions column={1} size="small" bordered>
          {Object.entries(obj).map(([key, val]) => (
            <Descriptions.Item label={key} key={key}>{String(val)}</Descriptions.Item>
          ))}
        </Descriptions>
      );
    } catch (e) {
      return payloadStr;
    }
  };

  return (
    <div className="profile-approval-section">
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="我的申请" key="approvals">
          <div style={{ marginBottom: 10 }}>
             <Radio.Group value={statusFilter} onChange={e => setStatusFilter(e.target.value)} size="small">
               <Radio.Button value="PENDING">待审批</Radio.Button>
               <Radio.Button value="HISTORY">已审批</Radio.Button>
             </Radio.Group>
          </div>
          <div className="approval-list-container">
            <List
              loading={loading}
              dataSource={approvals}
              renderItem={item => (
                <div className="approval-item" onClick={() => showDetail(item)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 'bold' }}>{item.typeDescription}</span>
                    <Tag color={statusMap[item.status]?.color}>{statusMap[item.status]?.text || item.status}</Tag>
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                    {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                  </div>
                  <div style={{ marginTop: 4 }}>{item.remark || '-'}</div>
                </div>
              )}
            />
          </div>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            simple
            style={{ textAlign: 'center', marginTop: 10 }}
          />
        </TabPane>
        <TabPane tab="消息通知" key="messages">
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
            暂无消息
          </div>
        </TabPane>
        {isAdmin && (
          <TabPane tab="请审批" key="to_approve">
            <div className="approval-list-container">
              <List
                loading={loading}
                dataSource={approvals}
                renderItem={item => (
                  <div className="approval-item" onClick={() => showDetail(item)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 'bold' }}>{item.typeDescription}</span>
                      <Tag color={statusMap[item.status]?.color}>{statusMap[item.status]?.text || item.status}</Tag>
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                      <span style={{ marginRight: 8 }}>申请人ID: {item.applicantId}</span>
                      {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                    </div>
                    <div style={{ marginTop: 4 }}>{item.remark || '-'}</div>
                  </div>
                )}
              />
            </div>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              simple
              style={{ textAlign: 'center', marginTop: 10 }}
            />
          </TabPane>
        )}
      </Tabs>

      <Modal
        title="申请详情"
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
          (activeTab === 'to_approve' && selectedApproval?.status === 'PENDING') && (
            <Button key="reject" danger onClick={handleReject} loading={actionLoading}>拒绝</Button>
          ),
          (activeTab === 'to_approve' && selectedApproval?.status === 'PENDING') && (
            <Button key="approve" type="primary" onClick={handleApprove} loading={actionLoading}>同意</Button>
          )
        ]}
      >
        {selectedApproval && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="类型">{selectedApproval.type}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[selectedApproval.status]?.color}>
                {statusMap[selectedApproval.status]?.text}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">
              {dayjs(selectedApproval.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="备注">{selectedApproval.remark}</Descriptions.Item>
            <Descriptions.Item label="审批意见">{selectedApproval.auditRemark || '-'}</Descriptions.Item>
            <Descriptions.Item label="详情数据">
              {renderPayload(selectedApproval.payload)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
